// Dynamic-import stale-chunk recovery logic, deliberately kept React-free so it is
// unit-testable in this repo — this repo has no React install (React is an implicit
// peer supplied by the parent app; see AGENTS.md's "Consumption model"), so any value
// import from 'react' would make this module unresolvable under `pnpm test` here.
// The only reference to React below is the `import type` on the next line, which is
// erased at compile time and never triggers module resolution. `lazy-with-reload.ts`
// is the thin wrapper that actually calls `React.lazy`. This mirrors a refactor the
// parent app's docs/TODO.md already records as wanted: "Extract dynamic-import
// recovery out of the logging module → src/utils/dynamic-import-recovery.ts".
import type { ComponentType } from 'react';

// A valid lazy module's `default` must be a renderable component type: either a function
// (function and class components) or one of React's object component types — memo,
// forwardRef and lazy wrappers — which React identifies by the `$$typeof` marker it
// stamps on them. Anything else (null, a string/number/boolean, or a PLAIN object with no
// `$$typeof`, e.g. `{}`) is not renderable and would crash React with a generic
// "element type is invalid" error, so it must fall through to the stale-chunk recovery
// below instead of being handed to React.
//
// The `$$typeof` requirement matters: checking only `typeof value === 'object'` would let
// a plain `{}` through and defeat the recovery. Every legitimate object-shaped component
// type carries the marker, and HOC-produced defaults in this repo return plain functions
// (e.g. `withSurveyContext`, `withTranslation()`), so this rejects nothing valid.
function isRenderableDefault(value: unknown): boolean {
  if (typeof value === 'function') {
    return true;
  }
  return typeof value === 'object' && value !== null && '$$typeof' in value;
}

const DYNAMIC_IMPORT_ERROR_SIGNATURES: readonly string[] = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk', // e.g. "Loading chunk 559 failed."
  'ChunkLoadError', // legacy/webpack safety net
];

export function isDynamicImportError(text: string | null | undefined): boolean {
  if (typeof text !== 'string' || !text) {
    return false;
  }
  return DYNAMIC_IMPORT_ERROR_SIGNATURES.some((sig) => text.includes(sig));
}

// This module is the single reload guard for its consumers: pureprofile-app's log.ts imports
// reloadOnce() from here for its vite:preloadError listener, and its lazyWithReload wraps
// loadWithReload() from here. Both layers therefore share this sessionStorage key AND the
// in-document reloadPending flag below by construction. Don't reintroduce a second copy of
// reloadOnce() in a consumer: a copy shares the key but not the flag, which lets a concurrent
// failure read the freshly-armed guard as persistent and redirect over a reload in flight.
const RELOAD_TS_KEY = 'pp:dynamic-import-reload-ts';
const RELOAD_GUARD_MS = 15_000;

// Set once THIS document has started a reload navigation. A later chunk failure in the same
// document must wait for that navigation rather than read the (just-armed) guard as a
// persistent failure — otherwise it redirects to the consumer's error page and cancels the reload.
let reloadPending = false;

// A document restored from the back/forward cache keeps its module state, so a reload it
// started before being cached would leave reloadPending stuck true and every later chunk
// failure would suspend forever. A restored page is a fresh chance to recover: clear it.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted) {
      reloadPending = false;
    }
  });
}

// Test-only: the pending flag is module state and outlives a single test.
export function resetReloadPendingForTests(): void {
  reloadPending = false;
}

// Returns whether this document is navigating away because of a stale-chunk reload —
// either just started by this call, or already started earlier in this document by a
// concurrent chunk failure. Callers must treat `true` as "suspend, do not report": the
// assignment to `location.href` below is asynchronous, so a second failure racing in on
// the same tick would otherwise see the freshly-armed sessionStorage guard, read it as a
// *persisted* failure, and report/redirect on top of the reload already under way.
// `false` means the guard was armed by a genuinely previous document (or sessionStorage is
// blocked and a refresh was already attempted): a real, persistent failure the caller should
// report.
export function reloadOnce(): boolean {
  if (reloadPending) {
    return true;
  }
  const now = Date.now();
  let sessionStorageOk = true;
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
    if (last && now - last < RELOAD_GUARD_MS) {
      return false;
    }
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
  } catch {
    sessionStorageOk = false;
  }
  const currentParams = new URLSearchParams(window.location.search);
  if (!sessionStorageOk && currentParams.get('refresh') !== null) {
    return false;
  }
  // Preserve the existing query string (survey URLs carry tokens and other params)
  // and just set/replace the `refresh` marker on top of it.
  currentParams.set('refresh', String(now));
  reloadPending = true;
  window.location.href = `${window.location.origin}${window.location.pathname}?${currentParams.toString()}`;
  return true;
}

// Consumer-supplied policy for the two decisions this module deliberately does NOT own,
// so a consuming app can reuse the recovery logic without also inheriting this repo's
// reporting and routing choices. Both are optional; the defaults are what the survey tool
// itself uses.
export interface RecoveryOptions {
  /**
   * Reports a stale-chunk failure that persisted past the one-shot reload guard.
   * Defaults to `console.error`. pureprofile-app passes its `logWarn` so the entry is
   * shipped to telemetry at warn level directly, rather than relying on that app's
   * `console.error` interception plus level downgrade.
   */
  report?: (message: string) => void;
  /**
   * Policy for a failure that persists past the reload guard. Whatever it resolves to is
   * handed straight to `React.lazy`, so it must do exactly one of:
   *
   * 1. **throw** — surfaces the failure to the consuming app's error boundary. This is the
   *    default, and what the survey tool itself uses;
   * 2. **return a promise that never resolves**, having navigated away — React then keeps
   *    the boundary suspended instead of rendering a broken module. pureprofile-app does
   *    this, redirecting to its own `/error` page;
   * 3. **resolve to a renderable fallback module** (`{ default: SomeComponent }`) — React
   *    renders that component in place of the chunk that failed.
   *
   * What it must NOT do is resolve to a non-renderable value, which would defeat the
   * `isRenderableDefault` guard this module applies to the real chunk.
   *
   * Routing deliberately belongs to the app rather than this library: the three consumers
   * have different route tables, so a `/error` redirect cannot live here.
   */
  onGuardExhausted?: <T extends ComponentType<any>>(thrown: unknown) => Promise<{ default: T }>;
}

function defaultReport(message: string): void {
  // `[survey-tool]` prefix makes the origin obvious in a consuming app's log store.
  console.error(`[survey-tool] ${message}`);
}

function defaultOnGuardExhausted<T extends ComponentType<any>>(thrown: unknown): Promise<{ default: T }> {
  throw thrown;
}

// Exported for unit testing and for consumers composing their own `lazy` wrapper.
export async function loadWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  options: RecoveryOptions = {}
): Promise<{ default: T }> {
  const report = options.report ?? defaultReport;
  const onGuardExhausted = options.onGuardExhausted ?? defaultOnGuardExhausted;
  try {
    const module = await factory();
    if (module && isRenderableDefault(module.default)) {
      return module;
    }
    // Resolved but empty/malformed module → fall through to the stale-chunk recovery.
  } catch (error) {
    const text = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    if (!isDynamicImportError(text)) {
      throw error; // genuine module-eval error, not a chunk-load failure
    }
    // Dynamic-import rejection → reload once to pull the current bundle.
    if (reloadOnce()) {
      // Navigating away — suspend forever so React never renders a broken module.
      // eslint-disable-next-line no-empty-function -- intentionally never resolves
      return await new Promise<{ default: T }>(() => {});
    }
    report(`lazyWithReload: dynamic import failed after reload guard exhausted: ${text}`);
    return await onGuardExhausted<T>(error);
  }
  // Resolved but empty/malformed module (no exception thrown) → same reload recovery.
  if (reloadOnce()) {
    // eslint-disable-next-line no-empty-function -- intentionally never resolves
    return await new Promise<{ default: T }>(() => {});
  }
  report('lazyWithReload: module resolved without a renderable default after reload guard exhausted');
  // Hand on a recognized dynamic-import error so a consumer's policy can identify the cause.
  return await onGuardExhausted<T>(new Error('Importing a module script failed'));
}
